/**
 * The signed-in org's administrators, for the lock screen's "who to ask".
 *
 * GET /auth/api/v1/organizations/me/admins. Asked once per page load; a
 * failure or an empty answer resolves to [] and the lock screen simply leaves
 * the section out (never a made-up contact).
 */
export const ORG_ADMINS_PATH = '/auth/api/v1/organizations/me/admins';

let cached = null;

const pickList = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  return [data.admins, data.items, data.data, data.results].find(Array.isArray) || [];
};

const nameOf = (a) => {
  const full = [a.first_name, a.last_name].filter(Boolean).join(' ').trim();
  return String(a.name || a.full_name || a.display_name || full || a.username || '').trim();
};

/** [{ name, email }] with a usable email, de-duplicated, in the service's order. */
export function normalizeAdmins(data) {
  const seen = new Set();
  return pickList(data)
    .filter((a) => a && typeof a === 'object')
    .map((a) => ({ name: nameOf(a), email: String(a.email || '').trim() }))
    .filter((a) => /^[^\s@]+@[^\s@]+$/.test(a.email))
    .filter((a) => {
      const key = a.email.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function fetchOrgAdmins() {
  if (!cached) {
    cached = import('../../services/api/apiClient')
      .then(({ default: api }) => api.get(ORG_ADMINS_PATH, { skipModuleLock: true }))
      .then((res) => normalizeAdmins(res?.data))
      .catch(() => {
        cached = null; // let a later lock screen try again
        return [];
      });
  }
  return cached;
}

/** Tests only. */
export function resetOrgAdminsCache() {
  cached = null;
}
